/**
 * MarkdownText — the ONE markdown renderer for LLM-authored chat text.
 *
 * The Assistant (and the smart-page chatbot) answers in markdown: headings,
 * bold, bullet/numbered lists, GFM tables, code, links. Rendered as raw text
 * those answers read as `**Full Name:**` and pipe-delimited table rows, so
 * every surface that shows an assistant reply renders it through here:
 * frontend/src/assistant/AssistantChat.tsx, its runtime/client twin, and
 * <ChatbotRenderer> in this package.
 *
 * Deliberately dependency-free (no react-markdown/remark): the runtime client
 * and the smart-page chatbot ship to end users, and the two AssistantChat
 * copies are kept dependency-identical. It builds React elements — never
 * dangerouslySetInnerHTML — so an LLM reply can't inject markup.
 *
 * Supported: ATX headings, paragraphs, `-`/`*`/`+` and `1.` lists (nested by
 * indent), GFM tables (with alignment), fenced + indented code, blockquotes,
 * horizontal rules, and inline code / bold / italic / strikethrough / links /
 * images / bare URLs / backslash escapes.
 *
 * One OPT-IN extension: `renderMarker` hands `[1]` / `[2, 5]` citation markers
 * back to the host mid-sentence, so a grounded answer (the document
 * repository's "Ask a question" panel) can make them clickable without first
 * splitting the text on them — which is what used to cost that panel its
 * formatting. Pass nothing and markers stay the plain text they always were.
 *
 * Colours come from `--md-*` custom properties with sane fallbacks, so a host
 * bubble can retint the borders and code chips without forking this file.
 */
import type { CSSProperties, ReactNode } from 'react'

const BORDER = 'var(--md-border, rgba(0,0,0,0.15))'
const CODE_BG = 'var(--md-code-bg, rgba(0,0,0,0.06))'
const MUTED = 'var(--md-muted, rgba(0,0,0,0.55))'
const HEAD_BG = 'var(--md-head-bg, rgba(0,0,0,0.04))'
const LINK = 'var(--md-link, #4f46e5)'

/* ── inline ───────────────────────────────────────────────────────────── */

const RE_CODE = /^(`+)([\s\S]*?)\1(?!`)/
const RE_IMAGE = /^!\[([^\]]*)\]\(\s*<?([^)\s>]*)>?(?:\s+"[^"]*")?\s*\)/
const RE_LINK = /^\[((?:[^[\]]|\[[^\]]*\])*)\]\(\s*<?([^)\s>]*)>?(?:\s+"[^"]*")?\s*\)/
const RE_STRONG = /^(\*\*|__)(?=\S)([\s\S]*?\S)\1/
const RE_EM = /^(\*|_)(?=\S)([\s\S]*?\S)\1(?!\1)/
const RE_DEL = /^~~(?=\S)([\s\S]*?\S)~~/
const RE_AUTOLINK = /^(https?:\/\/[^\s<>[\]()]+[^\s<>[\]().,;:!?'"])/
const RE_ANGLE_LINK = /^<((?:https?|mailto):[^>\s]+)>/
/** A citation marker — `[1]`, `[2, 5]`. Recognised ONLY when the host passes
 *  `renderMarker`; with no renderer the scan falls straight through and the
 *  text reads exactly as it did before, which is what keeps this opt-in. */
const RE_MARKER = /^\[(\d+(?:\s*,\s*\d+)*)\]/

/**
 * Renders one citation marker found in the prose.
 *
 * A grounded answer cites its sources INSIDE its sentences, so the marker
 * cannot be lifted out and listed underneath without moving it away from the
 * clause it supports. `numbers` are the digits inside the brackets and `raw`
 * is the marker as written, for a host that would rather print it back.
 */
export type MarkerRenderer = (numbers: number[], raw: string) => ReactNode

/** Only these characters can begin an inline construct — everything else is
 *  copied through verbatim, which keeps the scan linear over plain prose. */
const SPECIALS = new Set(['`', '!', '[', '*', '_', '~', '\\', '<', 'h'])

/** Links an LLM emits are relative app paths or http(s)/mailto — anything else
 *  (javascript:, data:) is dropped back to plain text. */
function safeHref(href: string): string | null {
  const h = (href || '').trim()
  if (!h) return null
  if (/^(https?:|mailto:|tel:)/i.test(h)) return h
  if (/^[a-z][a-z0-9+.-]*:/i.test(h)) return null // some other scheme — refuse
  return h // relative / anchor
}

function parseInline(src: string, key: string,
                     marker?: MarkerRenderer): ReactNode[] {
  const out: ReactNode[] = []
  let buf = ''
  let n = 0
  const push = (node: ReactNode) => { flush(); out.push(node) }
  const flush = () => { if (buf) { out.push(buf); buf = '' } }
  const k = () => `${key}i${n++}`

  let i = 0
  while (i < src.length) {
    const ch = src[i]
    if (!SPECIALS.has(ch)) { buf += ch; i += 1; continue }
    const rest = src.slice(i)
    let m: RegExpExecArray | null

    if (ch === '\\') {
      // Backslash escape — the next char is literal (markdown punctuation).
      const next = src[i + 1]
      if (next && /[\\`*_{}[\]()#+\-.!~<>|]/.test(next)) { buf += next; i += 2 } else { buf += ch; i += 1 }
      continue
    }
    if (ch === '`' && (m = RE_CODE.exec(rest))) {
      const code = m[2].replace(/^ (.*) $/s, '$1')
      push(<code key={k()} style={CODE_STYLE}>{code}</code>)
      i += m[0].length; continue
    }
    if (ch === '!' && (m = RE_IMAGE.exec(rest))) {
      const src2 = safeHref(m[2])
      if (src2) {
        push(<img key={k()} src={src2} alt={m[1]} style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />)
        i += m[0].length; continue
      }
    }
    if (ch === '[' && (m = RE_LINK.exec(rest))) {
      const href = safeHref(m[2])
      const label = parseInline(m[1], `${key}l${n}`, marker)
      push(href
        ? <a key={k()} href={href} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>{label}</a>
        : <span key={k()}>{label}</span>)
      i += m[0].length; continue
    }
    if (ch === '[' && marker && (m = RE_MARKER.exec(rest))) {
      const numbers = m[1].split(',').map((d) => Number(d.trim()))
      push(<span key={k()}>{marker(numbers, m[0])}</span>)
      i += m[0].length; continue
    }
    if ((ch === '*' || ch === '_') && (m = RE_STRONG.exec(rest))) {
      push(<strong key={k()} style={{ fontWeight: 600 }}>{parseInline(m[2], `${key}s${n}`, marker)}</strong>)
      i += m[0].length; continue
    }
    if ((ch === '*' || ch === '_') && (m = RE_EM.exec(rest))) {
      push(<em key={k()}>{parseInline(m[2], `${key}e${n}`, marker)}</em>)
      i += m[0].length; continue
    }
    if (ch === '~' && (m = RE_DEL.exec(rest))) {
      push(<del key={k()}>{parseInline(m[1], `${key}d${n}`, marker)}</del>)
      i += m[0].length; continue
    }
    if (ch === '<' && (m = RE_ANGLE_LINK.exec(rest))) {
      const href = safeHref(m[1])
      if (href) {
        push(<a key={k()} href={href} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>{m[1]}</a>)
        i += m[0].length; continue
      }
    }
    if (ch === 'h' && (m = RE_AUTOLINK.exec(rest))) {
      push(<a key={k()} href={m[1]} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>{m[1]}</a>)
      i += m[0].length; continue
    }
    buf += ch; i += 1
  }
  flush()
  return out
}

const CODE_STYLE: CSSProperties = {
  background: CODE_BG, borderRadius: 4, padding: '0.1em 0.35em',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.9em',
}
const LINK_STYLE: CSSProperties = { color: LINK, textDecoration: 'underline', wordBreak: 'break-word' }

/* ── blocks ───────────────────────────────────────────────────────────── */

const RE_HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/
const RE_HR = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/
const RE_FENCE = /^ {0,3}(```+|~~~+)\s*([^\s`]*)/
const RE_BULLET = /^(\s*)([-*+])\s+(.*)$/
const RE_ORDERED = /^(\s*)(\d{1,9})[.)]\s+(.*)$/
const RE_QUOTE = /^ {0,3}> ?(.*)$/
const RE_TABLE_SEP = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/

type Align = 'left' | 'center' | 'right'

/** Split a table row on unescaped pipes, dropping the leading/trailing ones. */
function splitRow(line: string): string[] {
  const cells: string[] = []
  let cur = ''
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '\\' && line[i + 1] === '|') { cur += '|'; i += 1; continue }
    if (c === '|') { cells.push(cur); cur = '' } else cur += c
  }
  cells.push(cur)
  if (cells.length && !cells[0].trim()) cells.shift()
  if (cells.length && !cells[cells.length - 1].trim()) cells.pop()
  return cells.map((c) => c.trim())
}

function alignments(sep: string): Align[] {
  return splitRow(sep).map((c) => {
    const l = c.startsWith(':'); const r = c.endsWith(':')
    return l && r ? 'center' : r ? 'right' : 'left'
  })
}

function renderBlocks(lines: string[], key: string,
                      marker?: MarkerRenderer): ReactNode[] {
  const out: ReactNode[] = []
  let i = 0
  let n = 0
  const k = () => `${key}b${n++}`

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) { i += 1; continue }

    // Fenced code
    let m = RE_FENCE.exec(line)
    if (m) {
      // A character class keeps both fence characters (` and ~) literal.
      const closer = new RegExp('^ {0,3}[' + m[1][0] + ']{' + m[1].length + ',}\\s*$')
      const body: string[] = []
      i += 1
      while (i < lines.length && !closer.test(lines[i])) { body.push(lines[i]); i += 1 }
      i += 1 // closing fence (or EOF)
      out.push(<pre key={k()} style={PRE_STYLE}><code>{body.join('\n')}</code></pre>)
      continue
    }

    if (RE_HR.test(line)) {
      out.push(<hr key={k()} style={{ border: 0, borderTop: `1px solid ${BORDER}`, margin: '10px 0' }} />)
      i += 1; continue
    }

    if ((m = RE_HEADING.exec(line))) {
      const level = m[1].length
      const Tag = (`h${Math.min(level + 2, 6)}`) as 'h3' | 'h4' | 'h5' | 'h6'
      const size = [1.15, 1.08, 1.0, 0.95, 0.92, 0.9][level - 1]
      out.push(
        <Tag key={k()} style={{
          margin: out.length ? '12px 0 4px' : '0 0 4px',
          fontSize: `${size}em`, fontWeight: 600, lineHeight: 1.3,
        }}>{parseInline(m[2], k(), marker)}</Tag>,
      )
      i += 1; continue
    }

    // Blockquote — collect the run, strip the markers, recurse.
    if (RE_QUOTE.test(line)) {
      const body: string[] = []
      let q: RegExpExecArray | null
      while (i < lines.length && (q = RE_QUOTE.exec(lines[i]))) { body.push(q[1]); i += 1 }
      out.push(
        <blockquote key={k()} style={{
          margin: '6px 0', padding: '2px 0 2px 10px',
          borderLeft: `3px solid ${BORDER}`, color: MUTED,
        }}>{renderBlocks(body, k(), marker)}</blockquote>,
      )
      continue
    }

    // GFM table — a header row followed by a `---|---` separator.
    if (line.includes('|') && i + 1 < lines.length && RE_TABLE_SEP.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const header = splitRow(line)
      const align = alignments(lines[i + 1])
      i += 2
      const rows: string[][] = []
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) { rows.push(splitRow(lines[i])); i += 1 }
      out.push(
        <div key={k()} style={{ overflowX: 'auto', margin: '6px 0' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.95em', width: '100%' }}>
            <thead>
              <tr>{header.map((h, c) => (
                <th key={c} style={{ ...CELL_STYLE, background: HEAD_BG, fontWeight: 600, textAlign: align[c] || 'left' }}>
                  {parseInline(h, `${k()}h${c}`, marker)}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>{header.map((_, c) => (
                  <td key={c} style={{ ...CELL_STYLE, textAlign: align[c] || 'left' }}>
                    {parseInline(r[c] ?? '', `${key}t${ri}c${c}`, marker)}
                  </td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    // Lists — consume the whole run (any marker), nesting by indent.
    if (RE_BULLET.test(line) || RE_ORDERED.test(line)) {
      const [node, next] = renderList(lines, i, k(), marker)
      out.push(node); i = next; continue
    }

    // Paragraph — up to the next blank line or block starter.
    const para: string[] = []
    while (i < lines.length && lines[i].trim()
      && !RE_HEADING.test(lines[i]) && !RE_HR.test(lines[i]) && !RE_FENCE.test(lines[i])
      && !RE_QUOTE.test(lines[i]) && !RE_BULLET.test(lines[i]) && !RE_ORDERED.test(lines[i])) {
      para.push(lines[i]); i += 1
    }
    if (!para.length) { para.push(lines[i]); i += 1 } // safety: always advance
    out.push(
      <p key={k()} style={{ margin: out.length ? '6px 0' : '0 0 6px', whiteSpace: 'pre-wrap' }}>
        {parseInline(para.join('\n').replace(/ {2,}\n/g, '\n'), k(), marker)}
      </p>,
    )
  }
  return out
}

/** Renders one list run starting at `start`; returns the node + the line after it. */
function renderList(lines: string[], start: number, key: string,
                    marker?: MarkerRenderer): [ReactNode, number] {
  const first = RE_BULLET.exec(lines[start]) || RE_ORDERED.exec(lines[start])!
  const baseIndent = first[1].length
  const ordered = !RE_BULLET.test(lines[start])
  const items: string[][] = []
  let i = start

  while (i < lines.length) {
    const line = lines[i]
    const m = RE_BULLET.exec(line) || RE_ORDERED.exec(line)
    if (m && m[1].length <= baseIndent + 1) {
      // A different marker family at this level starts a NEW list.
      if (items.length && (!RE_BULLET.test(line)) !== ordered) break
      items.push([m[3]])
      i += 1
      continue
    }
    if (!items.length) break
    if (!line.trim()) {
      // A blank line only continues the list if an indented line follows.
      const next = lines[i + 1]
      if (next && next.trim() && (next.length - next.trimStart().length) > baseIndent) { items[items.length - 1].push(''); i += 1; continue }
      break
    }
    const indent = line.length - line.trimStart().length
    if (indent > baseIndent) { items[items.length - 1].push(line.slice(baseIndent + 2)); i += 1; continue }
    break // lazy continuation of a sibling paragraph — end the list
  }

  const Tag = ordered ? 'ol' : 'ul'
  const startNo = ordered ? Number(first[2]) : undefined
  const node = (
    <Tag key={key} start={startNo && startNo !== 1 ? startNo : undefined} style={{ margin: '4px 0', paddingLeft: '1.35em' }}>
      {items.map((body, idx) => (
        <li key={idx} style={{ margin: '2px 0' }}>
          {renderItemBody(body, `${key}li${idx}`, marker)}
        </li>
      ))}
    </Tag>
  )
  return [node, i]
}

/** An item's body. The lead line stays inline so a tight item carries no <p>
 *  margins; whatever follows (a nested list, code, a second paragraph) goes
 *  back through the block renderer. */
function renderItemBody(body: string[], key: string,
                        marker?: MarkerRenderer): ReactNode {
  const trimmed = body.map((l) => l.replace(/\s+$/, ''))
  while (trimmed.length && !trimmed[trimmed.length - 1]) trimmed.pop()
  if (!trimmed.length) return null
  const lead = trimmed[0]
  const isBlockStart = RE_HEADING.test(lead) || RE_HR.test(lead) || RE_FENCE.test(lead)
    || RE_QUOTE.test(lead) || RE_BULLET.test(lead) || RE_ORDERED.test(lead)
  if (isBlockStart) return renderBlocks(trimmed, key, marker)
  if (trimmed.length === 1) return parseInline(lead, key, marker)
  return <>{parseInline(lead, key, marker)}{renderBlocks(trimmed.slice(1), `${key}r`, marker)}</>
}

const CELL_STYLE: CSSProperties = {
  border: `1px solid ${BORDER}`, padding: '4px 8px', verticalAlign: 'top',
}
const PRE_STYLE: CSSProperties = {
  background: CODE_BG, borderRadius: 8, padding: '8px 10px', margin: '6px 0',
  overflowX: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '0.85em', lineHeight: 1.45, whiteSpace: 'pre',
}

/* ── component ────────────────────────────────────────────────────────── */

export interface MarkdownTextProps {
  /** The raw markdown an LLM produced. */
  text: string
  /** Wrapper style — the host supplies the bubble's font/colour. */
  style?: CSSProperties
  className?: string
  /**
   * Renders `[1]` / `[2, 5]` citation markers found in the prose. Omitted —
   * the case for every chat surface — markers are left as the plain text they
   * always were, so this changes nothing for a host that does not ask for it.
   */
  renderMarker?: MarkerRenderer
}

/** Renders LLM markdown as React elements. Safe to call on partial text while
 *  a reply is still streaming — an unclosed fence/table just renders as-is. */
export function MarkdownText({ text, style, className,
                               renderMarker }: MarkdownTextProps) {
  const src = (text || '').replace(/\r\n?/g, '\n')
  return (
    <div className={className} style={{ minWidth: 0, ...style }}>
      {renderBlocks(src.split('\n'), 'm', renderMarker)}
    </div>
  )
}

export default MarkdownText
